import {
  Context,
  forwardRef,
  useContext,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Layers } from 'three';

import { useCursor } from '@react-three/drei';
import { extend, Object3DNode } from '@react-three/fiber';
import { DOLLHOUSE_LAYER_INTERACTIVE, Instance } from '@spazious/dollhouse-three';
import { useEventCallback } from '@spazious/react-hooks';

import { ModelContext, ModelContextValue } from '../Model/ModelContext.js';

extend({ Instance });

export interface ModelProps extends Omit<Object3DNode<Instance, typeof Instance>, 'id'> {
  url: string;
  context?: Context<ModelContextValue | null>;
  collision?: boolean;
}

export const Model = forwardRef<Instance, ModelProps>(({ url, context, ...restProps }, ref) => {
  const instanceRef = useRef<Instance | null>(null);
  const ctx = useContext(context || ModelContext);

  if (!ctx) {
    throw new Error('No context provided');
  }

  const { subscribe, getParent } = ctx;

  useImperativeHandle(ref, () => instanceRef.current!, []);

  const parentInstances = getParent(url);

  useLayoutEffect(() => subscribe(url, instanceRef), [url, subscribe]);

  const [hovered, setHover] = useState(false);
  useCursor(hovered);

  const handlePointerMove = useEventCallback(() => {
    setHover(true);
  });

  const handlePointerOut = useEventCallback(() => {
    setHover(false);
  });

  const layers = useMemo(() => {
    const layers = new Layers();
    layers.enable(DOLLHOUSE_LAYER_INTERACTIVE);
    return layers;
  }, []);

  return (
    <instance
      ref={instanceRef}
      instances={parentInstances}
      layers={layers}
      {...restProps}
      onPointerMove={handlePointerMove}
      onPointerOut={handlePointerOut}
    />
  );
});

export default Model;
